import { useMemo } from 'react';
import { SeriesOptionsType } from 'highcharts';
import { DispersionChartProps } from '../../../../../../@types/data/types';
import { Space } from 'antd';
import { useTheme } from '../../../../../../providers/theme';
import { calculateMean } from '../../../../../../components/utils/statistics';
import { getColors } from '../../../../../../components/utils/getColors';
import { useSelected } from '../../../../../../providers/selectedContext';
import { Chart } from '../../../../../../components/chart/BaseChart';
import { ChartSpec } from '../../../../../../components/chart/chartSpec';

const MeanChart: React.FC<DispersionChartProps> = ({ permData, containerWidth, height, xAxisLabel = 'FZI' }) => {
  const { isDay } = useTheme();
  const colors = getColors(isDay);
  const { selectedInfo } = useSelected();

  const chartZones = useMemo(
    () =>
      selectedInfo.project?.correlation?.zones?.map((zone, index) => {
        return {
          ...zone,
          name: 'Rock Type ' + (index + 1)
        };
      }) || [],
    [selectedInfo.project?.correlation?.zones]
  );

  const zonesNames = useMemo(() => chartZones.map(zone => zone.name).concat('No rock type'), [chartZones]);

  const seriesData = useMemo(() => {
    const data = chartZones.map(zone => {
      const pointsInZone = permData.filter(
        point => point.xAxisValue >= zone.start && point.xAxisValue <= zone.end
      );

      const meanValue = calculateMean(pointsInZone.map(point => point.xAxisValue));

      return {
        name: zone.name,
        y: meanValue,
        // evita null: usa undefined quando não há cor
        color: zone.color ?? undefined
      };
    });

    const pointsOutsideZone = permData.filter(point => {
      return !chartZones.some(
        zone => point.xAxisValue >= zone.start && point.xAxisValue <= zone.end
      );
    });

    const meanOutsideZone = calculateMean(pointsOutsideZone.map(point => point.xAxisValue));

    data.push({
      name: 'No rock type',
      y: meanOutsideZone,
      color: '#808080'
    });

    return data;
  }, [permData, chartZones]);

  const spec: ChartSpec = useMemo(
    () => ({
      type: 'column',
      height,
      title: 'Mean of Rock Types',
      xAxis: { categories: zonesNames, crosshair: true },
      yAxis: { title: xAxisLabel, type: 'linear', min: 0 },
      plotOptions: {
        column: {
          pointPadding: 0.2,
          borderWidth: 0
        }
      },
      legend: true,
      series: [
        {
          name: `Mean ${xAxisLabel} per Rock Type`,
          type: 'column',
          data: seriesData.map(zone => ({
            name: zone.name,
            y: zone.y,
            color: zone.color ?? undefined
          }))
        }
      ] as SeriesOptionsType[]
    }),
    [zonesNames, seriesData, height, xAxisLabel]
  );

  return (
    <Space
      direction="vertical"
      style={{
        backgroundColor: 'transparent',
        marginTop: '1rem',
        border: `1px solid ${colors.border}`,
        borderRadius: '16px',
        transition: 'width 0.5s',
        width: containerWidth ?? '49%' // aplica containerWidth
      }}
    >
      <div style={{ padding: '0.75rem' }}>
        <Chart spec={spec} />
      </div>
    </Space>
  );
};

export default MeanChart;
